/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  Button,
  HStack,
  VStack,
  useToast,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Image,
  Text,
  Flex,
  Spacer,
  Container,
} from '@chakra-ui/react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import Schemas from '../api/schemas';
import { PublicKey } from '@solana/web3.js';
import Constants from '../constants';
import { useStorage } from '../api/cache';
import {
  DECIMALS,
  MINT_ADDRESS,
  SendTransaction,
  getAccountInfo,
} from '../api/qdot';
import {
  computeTotalCost,
  NavigationEvent,
  UiStates,
} from './stream.component';
import { BrowserView } from 'react-device-detect';
import { firebaseFacade } from '../api/firebase';
import { increment } from 'firebase/firestore';
import { getAssociatedTokenAddress } from '@solana/spl-token';
import Marquee from 'react-fast-marquee';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ShuffleOnIcon from '@mui/icons-material/ShuffleOn';
import RepeatIcon from '@mui/icons-material/Repeat';
import RepeatOneIcon from '@mui/icons-material/RepeatOne';
import VolumeMuteIcon from '@mui/icons-material/VolumeMute';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function isTrackPaid(track) {
  const { track_schema } = Schemas;
  return track[track_schema.price] > 0;
}

function notEnoughFund(track, accountBalance) {
  const balance = accountBalance / DECIMALS;
  return balance < computeTotalCost(track);
}

export const PlayerStates = {
  idle: 0,
  playing: 1,
  paused: 2,
};

const addHeadingZero = num => {
  return num > 9 ? num.toString() : `0${num}`;
};

export const getDisplayTimeBySeconds = seconds => {
  if (!isFinite(seconds)) {
    return null;
  }

  const min = Math.floor(seconds / 60);
  const minStr = addHeadingZero(min);
  const secStr = addHeadingZero(Math.floor(seconds % 60));
  const mmSs = `${minStr}:${secStr}`;
  return mmSs;
};

export function BottomUI(props) {
  const {
    queueArtUri,
    track,
    trackProgress,
    onSkipNext,
    onPlayerStateChange,
    playerState,
    bottomNav,
    onPushView,
  } = props;

  const { track_schema } = Schemas;

  return (
    <VStack
      height={'60px'}
      bg="yellow.400"
      position={'fixed'}
      spacing={0}
      bottom={bottomNav ? ['64px', '64px', '64px', '64px', '64px'] : '0px'}
      left={0}
      right={0}
      zIndex={1000}
    >
      {!bottomNav && (
        <Slider
          aria-label={'progress'}
          defaultValue={0}
          value={trackProgress}
          min={0}
          max={track[track_schema.duration]}
        >
          <SliderTrack height={'3px'}>
            <SliderFilledTrack bg={'yellow.400'} />
          </SliderTrack>
        </Slider>
      )}
      <Flex m={0} width={'100%'}>
        <Image
          width={'50px'}
          height={'50px'}
          p={'5px'}
          src={track.art_uri || queueArtUri}
          alt="Artwork"
          onClick={() => onPushView(new NavigationEvent(UiStates.one, {}))}
          cursor={'pointer'}
          borderRadius={'lg'}
        />
        <Spacer />
        <VStack
          spacing={0}
          onClick={() => onPushView(new NavigationEvent(UiStates.one, {}))}
          cursor={'pointer'}
          align={'center'}
        >
          {track.title && (
            <Marquee
              gradient={false}
              play={track.title && track.title.length > 11}
            >
              <Text fontSize={'lg'} fontWeight={'medium'}>
                {track.title}
              </Text>
            </Marquee>
          )}
          <Marquee gradient={false} play={false}>
            <Text fontSize={'sm'} fontWeight={'light'} noOfLines={1}>
              {track.artist}
            </Text>
          </Marquee>
        </VStack>
        <Spacer />
        <HStack spacing={0}>
          <Button onClick={onSkipNext} bg="transparent">
            <SkipNextIcon />
          </Button>
          <Button onClick={onPlayerStateChange} bg="transparent">
            {playerState !== PlayerStates.playing ? (
              <PlayArrowIcon />
            ) : (
              <PauseIcon />
            )}
          </Button>
        </HStack>
      </Flex>
      {bottomNav && (
        <Slider
          aria-label={'progress'}
          defaultValue={0}
          value={trackProgress}
          min={0}
          max={track[track_schema.duration]}
        >
          <SliderTrack height={'3px'}>
            <SliderFilledTrack bg={'yellow.400'} />
          </SliderTrack>
        </Slider>
      )}
      {bottomNav}
    </VStack>
  );
}

export function FullUI(props) {
  const {
    streamingq,
    queueArtUri,
    track,
    looping,
    trackProgress,
    shuffle,
    isMute,
    playerState,
    onSkipNext,
    onPlayerStateChange,
    onScrub,
    onShuffle,
    onSkipPrevious,
    onLoop,
    onVolumeChange,
    volume,
    onPopView,
  } = props;

  return (
    <Container
      color={'white'}
      marginBottom={'18px'}
      borderRadius={'lg'}
    >
      <VStack p={3}>
        <Button bg={'transparent'} color={"yellow.400"} onClick={onPopView}>
          <ExpandMoreIcon />
        </Button>
        <Text fontSize={'4xl'} fontWeight={'extrabold'} noOfLines={1}>
          {streamingq.name}
        </Text>
        <Image
          className="img-fluid"
          maxHeight={'75%'}
          maxWidth={'75%'}
          borderRadius={'2xl'}
          src={track.art_uri || queueArtUri}
          alt="Artwork"
        />
        <Text fontSize={'3xl'} fontWeight={'bold'}>
          {track.title}
        </Text>
        <Text fontSize={'xl'} fontWeight={'thin'}>
          {track.artist}
        </Text>
        <VStack spacing={'8px'}>
          <Slider
            aria-label={'progress'}
            defaultValue={0}
            value={trackProgress}
            min={0}
            max={track.duration}
            onChange={onScrub}
          >
            <SliderTrack>
              <SliderFilledTrack bg={'yellow.400'} />
            </SliderTrack>
            <SliderThumb aria-label={'scrubber'} boxSize={['16px']} index={0} />
          </Slider>
          <HStack aria-label="controls" spacing={[2, 5]}>
            <Button
              onClick={onShuffle}
              bg="transparent"
              color={shuffle ? 'yellow.400' : 'white'}
            >
              {shuffle ? <ShuffleOnIcon /> : <ShuffleIcon />}
            </Button>
            <Button onClick={onSkipPrevious} bg="transparent">
              <SkipPreviousIcon />
            </Button>
            <Button
              onClick={onPlayerStateChange}
              bg="gray.400"
              borderRadius={'50%'}
              width={'50px'}
              height={'50px'}
              opacity={'0.9'}
            >
              {playerState !== PlayerStates.playing ? (
                <PlayArrowIcon />
              ) : (
                <PauseIcon />
              )}
            </Button>
            <Button onClick={onSkipNext} bg="transparent">
              <SkipNextIcon />
            </Button>
            <Button
              onClick={onLoop}
              bg="transparent"
              color={looping ? 'yellow.400' : 'white'}
            >
              {looping ? <RepeatOneIcon /> : <RepeatIcon />}
            </Button>
          </HStack>
          <BrowserView>
            <HStack>
              {isMute ? <VolumeMuteIcon /> : <VolumeUpIcon />}
              <Slider
                aria-label="slider-ex-3"
                width={['150px', '250px']}
                min={0}
                max={1}
                step={0.001}
                defaultValue={volume}
                onChange={onVolumeChange}
              >
                <SliderTrack>
                  <SliderFilledTrack bg={'yellow.400'} />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </HStack>
          </BrowserView>
        </VStack>
      </VStack>
    </Container>
  );
}

export function Player(props) {
  const {
    PlayerUIDelegate,
    streamingq,
    streamingqAddress,
    isPlaying,
    loops,
    track,
    onPlayToggle,
    onNext,
    onPrev,
    onShuffle,
    onStateChange,
    subscribed,
    audioRef,
    setLooping,
    looping,
    isActive,
    onPay,
    paid,
    accountExists,
    trackAddressMap,
    associatedTokenAddress,
    accountBalance
  } = props;

  const [playerState, setPlayerState] = useState(PlayerStates.idle);
  const { track_schema } = Schemas;
  const { publicKey, signTransaction } = useWallet();

  const [shuffle, setShuffle] = useState(false);
  const toast = useToast();
  const {
    empty,
  } = Constants;

  const { connection } = useConnection();

  const intervalRef = useRef();
  const [trackProgress, setTrackProgress] = useState(0);
  const timeoutRef = useRef();

  const [isMute, setMute] = useState(false);
  const [queueStats, setQueueStats] = useState({});

  const payToPlay = async track => {
    if (paid) return true;
    const total = computeTotalCost(track);
    const balance = Number.parseInt(accountBalance) / DECIMALS;
    if (total > Number.parseInt(accountBalance)) {
      if (isActive) {
        toast({
          title: 'Not Enough Fund',
          description: `The track you're about to play costs ${total} QDOT while your balance is ${balance} QDOT`,
          duration: 5000,
          isClosable: true,
          status: 'info',
          position: 'top',
        });
      }
      setPlayerState(PlayerStates.idle);
      return false;
    }

    const result = associatedTokenAddress
      ? await SendTransaction(
          connection,
          new PublicKey(associatedTokenAddress),
          publicKey,
          new PublicKey(track.owner_wallet_address),
          total,
          signTransaction
        )
      : false;

    if (!result && isActive)
      toast({
        title: 'Transaction Failed',
        description: `The transaction failed.`,
        duration: 5000,
        isClosable: true,
        status: 'info',
        position: 'top',
      });
    onPay(result);
    return result;
  };

  const play = async () => {
    if (track === empty) {
      if (isActive) {
        toast({
          title: 'Empty Queue',
          description: 'This queue is empty. Add tracks to it!',
          duration: 5000,
          isClosable: true,
          status: 'error',
          position: 'top',
        });
      }
      setPlayerState(PlayerStates.idle);
      onStateChange(PlayerStates.idle);
      return;
    }

    if (!subscribed) {
      const balance = Number.parseInt(accountBalance);
      if (isTrackPaid(track) && !publicKey) {
        if (isActive) {
          toast({
            title: 'Paid Track',
            description:
              "The track you're trying to play is not free. Connect Wallet to play!",
            duration: 5000,
            isClosable: true,
            status: 'info',
            position: 'top',
          });
        }
        setPlayerState(PlayerStates.idle);
        onStateChange(PlayerStates.idle);
        return;
      }

      if (isTrackPaid(track) && !accountExists) {
        if (isActive) {
          toast({
            title: 'No QDOT account',
            description: `You do not hava a QDOT account. Please open an account by adding QDOT to your token list.`,
            duration: 5000,
            isClosable: true,
            status: 'info',
            position: 'top',
          });
        }
        setPlayerState(PlayerStates.idle);
        onStateChange(PlayerStates.idle);
        return;
      }

      if (
        notEnoughFund(track, balance) &&
        publicKey !== null &&
        publicKey.toBase58() !== track[track_schema.owner_wallet_address]
      ) {
        if (isActive) {
          toast({
            title: 'Not Enough Fund',
            description: `The track you're about to play costs ${computeTotalCost(
              track
            )} QDOT while your balance is ${balance / DECIMALS} QDOT`,
            duration: 5000,
            isClosable: true,
            status: 'info',
            position: 'top',
          });
        }
        setPlayerState(PlayerStates.idle);
        onStateChange(PlayerStates.idle);
        return;
      }

      if (
        isTrackPaid(track) &&
        publicKey !== null &&
        publicKey.toBase58() !== track.owner_wallet_address
      ) {
        const result = await payToPlay(track);
        if (!result) {
          setPlayerState(PlayerStates.idle);
          onStateChange(PlayerStates.idle);
          return;
        }
        firebaseFacade.updateMonthlyGrossing(
          streamingq,
          computeTotalCost(track)
        );
      }
    }

    startTimer();
    audioRef.current.play();

    setPlayerState(PlayerStates.playing);
    onStateChange(PlayerStates.playing);
  };

  const pause = () => {
    clearInterval(intervalRef.current);
    audioRef.current.pause();
    setPlayerState(PlayerStates.paused);

    onStateChange(PlayerStates.paused);
  };

  const onSkipNext = useCallback(() => {
    onNext();
  }, [track]);

  const onSkipPrevious = useCallback(() => {
    onPrev();
  }, [track]);

  const handleEnded = useCallback(() => {
    clearInterval(intervalRef.current);
    onSkipNext();
  }, [streamingq, track]);

  const startTimer = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      updateLoopCount();
      setTrackProgress(audioRef.current.currentTime);
    }, 1000);
  }, [intervalRef, track]);

  const onScrub = progress => {
    clearInterval(intervalRef.current);
    setTrackProgress(progress);
    scheduleScrubbEnd(progress);
  };

  const scheduleScrubbEnd = progress => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onScrubEnd(progress), 500);
  };

  const onScrubEnd = progress => {
    audioRef.current.currentTime = progress;
    if (playerState !== PlayerStates.playing) play();
    startTimer();
  };

  const handleShuffle = () => {
    setShuffle(!shuffle);
    onShuffle(!shuffle);
  };

  const onVolumeChange = vol => {
    setMute(vol === 0);
    audioRef.current.volume = vol;
  };

  const handleLoop = () => {
    setLooping(!looping);
  };

  const onPlayerStateChange = _ev => {
    if (playerState === PlayerStates.idle) {
      onStateChange(PlayerStates.playing);
      onPlayToggle(streamingqAddress, track.track_address, true, trackAddressMap);
    } else if (playerState === PlayerStates.paused) play();
    else pause();
  };

  const updateLoopCount = useCallback(async () => {
    const { db_node_schema } = Schemas;
    const docPath = `${db_node_schema.queues}/${streamingq.queue_address}/${db_node_schema.tracks}/${track.track_address}`;

    const diff = Math.ceil(track.duration - audioRef.current.currentTime);
    if (track.duration > 1 && diff <= 1) {
      try {
        await firebaseFacade.updateDocument(docPath, { loops: increment(1) });
      } catch (error) {}
    }
  }, [track]);

  const clearTasks = () => {
    clearInterval(intervalRef.current);
    clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    firebaseFacade.loadStats(streamingq).then(setQueueStats);
  }, [streamingq]);

  useEffect(() => {
    audioRef.current.onended = handleEnded;
    if (isPlaying && isActive) {
      if (paid) {
        startTimer();
        setPlayerState(PlayerStates.playing);
      } else {
        play();
      }
      audioRef.current.onerror = audioRef.current.onabort = null;
    } else if (playerState !== PlayerStates.idle && isActive) {
      pause();
    }
    return clearTasks;
  }, [track, loops, isPlaying, audioRef, isActive]); // using loops to trigger effect for queue with a single track

  return (
    <PlayerUIDelegate
      {...props}
      track={track}
      looping={looping}
      queueStats={queueStats}
      trackProgress={trackProgress}
      shuffle={shuffle}
      isMute={isMute}
      playerState={playerState}
      onSkipNext={onSkipNext}
      onPlayerStateChange={onPlayerStateChange}
      onScrub={onScrub}
      onShuffle={handleShuffle}
      onSkipPrevious={onSkipPrevious}
      onLoop={handleLoop}
      onVolumeChange={onVolumeChange}
      volume={audioRef.current && audioRef.current.volume}
    />
  );
}
